import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

// Firebase configuration validation
function validateFirebaseConfig(config: Record<string, string>): config is Record<string, string> {
  const requiredFields = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];

  if (!config || typeof config !== 'object') {
    return false;
  }

  for (const field of requiredFields) {
    if (!config[field] || typeof config[field] !== 'string') {
      console.error(`Missing or invalid Firebase config field: ${field}`);
      return false;
    }
  }

  return true;
}

// Parse and validate Firebase configuration at build time
function getFirebaseConfig() {
  let config;

  // Try parsing VITE_FIREBASE_CONFIG first
  if (import.meta.env.VITE_FIREBASE_CONFIG) {
    try {
      config = JSON.parse(import.meta.env.VITE_FIREBASE_CONFIG);
    } catch (error) {
      console.error('Invalid JSON in VITE_FIREBASE_CONFIG:', error);
      throw new Error(`VITE_FIREBASE_CONFIG contains invalid JSON: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  } else {
    // Fall back to individual environment variables
    config = {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
      measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
    };
  }

  // Validate the configuration
  if (!validateFirebaseConfig(config)) {
    console.error('Firebase configuration validation failed. Config:', config);
    throw new Error('Invalid Firebase configuration. Please check your environment variables.');
  }

  return config;
}

// Get validated Firebase configuration
const firebaseConfig = getFirebaseConfig();

// Initialize Firebase
export const app = initializeApp(firebaseConfig);
export const analytics = getAnalytics(app);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

// Connect to Firebase Emulators if in development mode
const useEmulator = import.meta.env.VITE_USE_EMULATOR === 'true';

if (useEmulator) {
  console.log('🔧 Connecting to Firebase Emulators...');

  // Connect Auth Emulator - use localhost for consistency with Vite dev server
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });

  // Connect Firestore Emulator
  connectFirestoreEmulator(db, 'localhost', 8080);

  // Connect Storage Emulator
  connectStorageEmulator(storage, 'localhost', 9199);

  // Connect Functions Emulator
  connectFunctionsEmulator(functions, 'localhost', 5001);

  console.log('✅ Connected to Firebase Emulators');
  console.log('   - Auth: http://localhost:9099');
  console.log('   - Firestore: http://localhost:8080');
  console.log('   - Storage: http://localhost:9199');
  console.log('   - Functions: http://localhost:5001');
  console.log('   - UI: http://localhost:4000');
} else {
  console.log('🌐 Using production Firebase');
}
